import { useMutation } from '@tanstack/react-query';
import { Endpoint } from '@/constants';
import { postData } from '@/utils/postData';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import type { GooglePayOrder } from '@/types/googlePay';

interface GooglePayOrderResponse {
  isSuccess: boolean;
  data?: {
    redirectUrl?: string;
    errorMessage?: string | null;
    threeDSecureAuthenticationRequired?: boolean;
    threeDSecureRedirectUrl?: string;
  };
  messages?: string[];
  salesOrderError?: {
    message?: string;
    redirectUrl?: string;
  };
  orderNumber?: string;
}

interface BuyNowGooglePayData {
  order: GooglePayOrder;
  productCode: string;
  quantity: number;
}

const createBuyNowGooglePayOrder = ({order, quantity }: BuyNowGooglePayData): Promise<GooglePayOrderResponse> =>
  postData(Endpoint.CreateBuyNowGooglePayOrder)({
    ...order,
    Quantity: quantity
  } as unknown as Record<string, unknown>);

interface UseGooglePayBuyNowOptions {
  onThreeDSecureRequired?: (url: string) => void;
  productCode: string;
  quantity?: number;
}

export const useGooglePayBuyNow = (options: UseGooglePayBuyNowOptions) => {
  const { onThreeDSecureRequired, productCode, quantity = 1 } = options;
  
  const { isPending, mutate } = useMutation<
    GooglePayOrderResponse,
    Error,
    GooglePayOrder
  >({
    mutationFn: (order) => createBuyNowGooglePayOrder({ order, productCode, quantity }),
    onError: error => {
      console.error(`Failed to submit Google Pay Buy Now payment: ${error.message}`);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Payment failed. Please try again or use a different payment method.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
    onSuccess: async response => {
      console.log('Google Pay Buy Now: Order response received', response);
      
      // Handle sales order error
      if (response.salesOrderError) {
        const errorMessage = response.salesOrderError.message || 'Payment processing failed. Please try again.';
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        
        // If there's a redirect URL in the error, redirect after delay
        if (response.salesOrderError.redirectUrl) {
          setTimeout(() => {
            window.location.href = response.salesOrderError?.redirectUrl ?? '/';
          }, 3000);
        }
        return;
      }
      
      // Handle 3D Secure
      const threeDSRequired = response.data?.threeDSecureAuthenticationRequired;
      const threeDSUrl = response.data?.threeDSecureRedirectUrl;

      if (threeDSRequired && threeDSUrl) {
        console.log('Google Pay Buy Now: 3D Secure authentication required');
        if (onThreeDSecureRequired) {
          onThreeDSecureRequired(threeDSUrl);
        } else {
          // Fallback to redirect if no handler provided
          window.location.href = threeDSUrl;
        }
        return;
      }
      
      // Handle successful order with nested data structure
      if (response.isSuccess && response.data?.redirectUrl) {
        console.log('Google Pay Buy Now: Order created successfully, redirecting to:', response.data.redirectUrl);
        
        // For Buy Now, we don't need to refresh checkout data as we're using a separate cart
        // Redirect to confirmation page
        window.location.href = response.data.redirectUrl;
        return;
      }
      
      // Handle successful order with order number (legacy response format)
      if (response.orderNumber) {
        // Build confirmation URL
        const confirmationUrl = `/checkout/order-confirmation?confirmationId=${response.orderNumber}`;
        window.location.href = confirmationUrl;
        return;
      }
      
      // Handle redirect URL at root level (legacy response format)
      if (response.data?.redirectUrl) {
        window.location.href = response.data?.redirectUrl ?? '/';
        return;
      }
      
      // Handle error message in data
      if (response.data?.errorMessage) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.data.errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Handle generic failure
      if (!response.isSuccess && response.messages && response.messages.length > 0) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.messages[0]}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Unexpected response
      console.error('Google Pay Buy Now: Unexpected response format', response);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              An unexpected error occurred. Please try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
  });

  return { isPending, createBuyNowGooglePayOrder: mutate };
};